<script setup>
/**
 * Popover — drop-in replacement for BV2's b-popover.
 *
 * Usage: wrap the trigger element in the default slot; put popover content in #content slot.
 *
 *   <Popover placement="bottom" trigger="click" custom-class="my-class">
 *       <button>Trigger</button>
 *       <template #content>Rich content here</template>
 *   </Popover>
 *
 * Props:
 *   customClass — extra class on the popover element
 *   placement   — 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 *   trigger     — 'hover' | 'click' (default: 'hover')
 */
import { ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import Popper from 'popper.js';

const emit = defineEmits(['click-outside']);

const props = defineProps({
    customClass: { type: String, default: '' },
    placement:   { type: String, default: 'top' },
    trigger:     { type: String, default: 'hover' },
    target:      { type: String, default: '' }, // element id to anchor Popper to (skips slot anchor)
});

const show     = ref(false);
const anchorEl = ref(null);
const contentEl = ref(null);
let   _popper  = null;
let   _docClickHandler = null;

// For trigger="manual": emit 'click-outside' when user clicks outside the popover content.
// Use nextTick so the listener is added after the current event cycle (avoids capturing
// the same click that triggered open()).
watch(show, (val) => {
    if (props.trigger !== 'manual') return;
    if (val) {
        nextTick(() => {
            _docClickHandler = (e) => {
                if (contentEl.value && !contentEl.value.contains(e.target)) {
                    close();
                    emit('click-outside', e);
                }
            };
            document.addEventListener('click', _docClickHandler);
        });
    } else {
        if (_docClickHandler) {
            document.removeEventListener('click', _docClickHandler);
            _docClickHandler = null;
        }
    }
});

function syncPlacementClass(placement) {
    if (!contentEl.value) return;
    const base = placement.split('-')[0];
    contentEl.value.className = contentEl.value.className
        .replace(/\bbs-popover-\S+/g, `bs-popover-${base}`);
}

function createPopper() {
    const anchor = props.target ? document.getElementById(props.target) : anchorEl.value;
    if (!anchor || !contentEl.value) return;
    destroyPopper();
    _popper = new Popper(anchor, contentEl.value, {
        placement: props.placement,
        modifiers: {
            arrow: { element: '.arrow' },
            preventOverflow: { boundariesElement: 'viewport' },
        },
        onCreate: (data) => syncPlacementClass(data.placement),
        onUpdate: (data) => syncPlacementClass(data.placement),
    });
}

function destroyPopper() {
    if (_popper) { _popper.destroy(); _popper = null; }
}

async function open() {
    show.value = true;
    await nextTick();
    createPopper();
}

function close() {
    show.value = false;
    destroyPopper();
}

function onEnter() { if (props.trigger === 'hover') open(); }
function onLeave() { if (props.trigger === 'hover') close(); }
function onClickTrigger() { if (props.trigger === 'click') show.value ? close() : open(); }
function onClickOutside() { if (props.trigger === 'click' && show.value) close(); }

onMounted(() => {
    if (contentEl.value) document.body.appendChild(contentEl.value);
});

defineExpose({ open, close });

onBeforeUnmount(() => {
    if (_docClickHandler) document.removeEventListener('click', _docClickHandler);
    destroyPopper();
    if (contentEl.value) contentEl.value.remove();
});
</script>

<template>
    <span
        ref="anchorEl"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @click.stop="onClickTrigger"
        v-click-outside="onClickOutside"
    >
        <slot />

        <div
            v-show="show"
            ref="contentEl"
            role="tooltip"
            :class="['popover', `bs-popover-${placement.split('-')[0]}`, customClass]"
        >
            <div class="arrow"></div>
            <div class="popover-body">
                <slot name="content" />
            </div>
        </div>
    </span>
</template>

<style scoped>
.popover {
    text-align: left;
}
</style>
